import {
  DriverAppEvents as DriverEventsDefault,
  OrderEvents as OrderEventsDefault,
  TaskEvents as TaskEventsDefault,
  TransactionEvents as TransactionEventsDefault,
  TripEvents as TripEventsDefault,
  WalletEvents as WalletEventsDefault
} from '../../../../core/middleware/events';
import { ALERT_TYPE } from '../../../../core/types/alerts';

export enum IntegrationFlowType {
  TDT = 'TDT'
}

interface ITDTIntegrationSettings {
  collection_time_threshold: number;
}
interface IIntegrationFlow {
  contract?: string;
  enabled: boolean;
  settings?: ITDTIntegrationSettings;
  type?: IntegrationFlowType;
}

class OrderEvents {
  collected: boolean;
  in_transit: boolean;
  arrived: boolean;
  delivered: boolean;
  abandoned: boolean;
  returned: boolean;
  cancelled: false;
  near_by: boolean;

  constructor(args) {
    this.collected = args.collected;
    this.in_transit = args.in_transit;
    this.arrived = args.arrived;
    this.delivered = args.delivered;
    this.abandoned = args.abandoned;
    this.returned = args.returned;
    this.cancelled = args.cancelled;
    this.near_by = args.near_by ?? false;
  }
}
class TripEvents {
  manual_assigned: boolean;
  auto_assigned: boolean;
  accepted: boolean;
  rejected: boolean;
  arrived: boolean;
  started: boolean;
  completed: boolean;
  cancelled: boolean;
  arrived_at_collection_point: boolean;
  in_progress: boolean;

  constructor(args) {
    this.manual_assigned = args.manual_assigned;
    this.auto_assigned = args.auto_assigned;
    this.accepted = args.accepted;
    this.rejected = args.rejected;
    this.arrived = args.arrived;
    this.started = args.started;
    this.completed = args.completed;
    this.cancelled = args.cancelled;
    this.arrived_at_collection_point = args.arrived_at_collection_point;
    this.in_progress = args.in_progress;
  }
}

class TaskEvents {
  collected: boolean;
  started: boolean;
  completed: boolean;
  abandoned: boolean;
  returned: boolean;
  arrived: boolean;
  cancelled: boolean;

  constructor(args) {
    this.collected = args.collected;
    this.started = args.started;
    this.completed = args.completed;
    this.abandoned = args.abandoned;
    this.returned = args.returned;
    this.arrived = args.arrived;
    this.cancelled = args.cancelled;
  }
}

class DriverEvents {
  online: boolean;
  offline: boolean;
  onlunch: boolean;
  endlunch: boolean;

  constructor(args) {
    this.online = args.online;
    this.offline = args.offline;
    this.onlunch = args.onlunch;
    this.endlunch = args.endlunch;
  }
}
class WalletEvents {
  completed: boolean;

  constructor(args) {
    this.completed = args.completed;
  }
}

class TransactionEvents {
  summary: boolean;

  constructor(args) {
    this.summary = args.summary;
  }
}
class Webhooks {
  location: string;
  general: string;
  transaction: string;
  order_events: OrderEvents;
  trip_events: TripEvents;
  task_events: TaskEvents;
  driver_events: DriverEvents;
  wallet_events: WalletEvents;
  transaction_events: TransactionEvents;

  constructor(
    location: string,
    general: string,
    transaction: string,
    order_events: any,
    trip_events: any,
    task_events: any,
    driver_events: any,
    wallet_events: any,
    transaction_events: any
  ) {
    this.location = location || '';
    this.general = general || '';
    this.transaction = transaction || '';
    this.order_events = new OrderEvents(order_events);
    this.trip_events = new TripEvents(trip_events);
    this.task_events = new TaskEvents(task_events);
    this.driver_events = new DriverEvents(driver_events);
    this.wallet_events = new WalletEvents(wallet_events);
    this.transaction_events = new TransactionEvents(transaction_events);
  }
}

export interface IAlertSetting {
  type: ALERT_TYPE;
  enabled: boolean;
  target: number;
  buffer: number;
}

class Organization {
  id: string;
  alerts: IAlertSetting[];
  name: string;
  logo: string;
  webhooks: Webhooks;
  allow_custom_payment_fields?: boolean;
  flightboard_enabled?: boolean;
  client_type: string;
  integration_flow: IIntegrationFlow;
  /**
   * Organization entity constructor
   * @constructor
   * @param {string} id
   * @param {string} name
   * @param {string} organization
   * @param {string} logo
   * @param {any} webhooks
   */
  constructor(
    id: string,
    name: string,
    logo: string,
    webhooks: any = undefined,
    client_type: string,
    integration_flow,
    allow_custom_payment_fields?: boolean,
    alerts: IAlertSetting[] = [],
    flightboard_enabled?: boolean
  ) {
    const {
      location,
      general,
      transaction,
      order_events,
      trip_events,
      task_events,
      driver_events,
      wallet_events,
      transaction_events
    } = webhooks || {};

    webhooks = {
      location: location || '',
      general: general || '',
      transaction: transaction || '',
      order_events: { ...OrderEventsDefault, ...order_events },
      trip_events: { ...TripEventsDefault, ...trip_events },
      task_events: { ...TaskEventsDefault, ...task_events },
      driver_events: { ...DriverEventsDefault, ...driver_events },
      wallet_events: { ...WalletEventsDefault, ...wallet_events },
      transaction_events: { ...TransactionEventsDefault, ...transaction_events }
    };
    this.id = id;
    this.name = name;
    this.logo = logo;
    this.webhooks = new Webhooks(
      webhooks.location,
      webhooks.general,
      webhooks.transaction,
      webhooks.order_events,
      webhooks.trip_events,
      webhooks.task_events,
      webhooks.driver_events,
      webhooks.wallet_events,
      webhooks.transaction_events
    );
    this.allow_custom_payment_fields = allow_custom_payment_fields || false;
    this.client_type = client_type;
    this.integration_flow = integration_flow ?? undefined;
    this.flightboard_enabled = flightboard_enabled || false;
    this.alerts = alerts;
  }
}

export class Me {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  mobile_no: string;
  image: string;
  organization: Organization;

  constructor(args: any) {
    this.id = args.id;
    this.firstname = args.firstname;
    this.lastname = args.lastname;
    this.email = args.email;
    this.mobile_no = args.mobile_no;
    this.image = args.image;
    this.organization = new Organization(
      args.organization.id,
      args.organization.name,
      args.organization.logo,
      args.organization.webhooks,
      args.organization.client_type,
      args.organization.integration_flow,
      args.organization.allow_custom_payment_fields,
      args.organization.alerts,
      args.organization.flightboard_enabled
    );
  }
}
